/**
 * Work-order time entries and timer (#2705).
 *
 * Office staff attribute a row to a booking service with `bookingServiceId`.
 * Null / omitted stays unassigned. Mutations refresh the work order so
 * actualDurationHours / actualActiveHours stay in sync. They never patch
 * planned hours or prices.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export interface WorkOrderTimeEntry {
  id: string;
  bookingId: string;
  mechanicId: string;
  mechanicName?: string | null;
  bookingServiceId: string | null;
  startedAt: string;
  endedAt: string | null;
  durationHours: string | number | null;
  breakHours: string | number;
  description: string | null;
  billable: boolean;
  laborRateCents: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkOrderTimeEntryData {
  startedAt: string;
  endedAt?: string;
  description?: string;
  bookingServiceId?: string | null;
}

export interface UpdateWorkOrderTimeEntryData {
  startedAt?: string;
  endedAt?: string;
  description?: string | null;
  bookingServiceId?: string | null;
}

function invalidateTimeEntryViews(
  queryClient: ReturnType<typeof useQueryClient>,
  workOrderId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(workOrderId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.timeEntries(workOrderId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.history(workOrderId) });
  void queryClient.invalidateQueries({ queryKey: [...queryKeys.bookings.all, 'calendar'] });
}

export function useWorkOrderTimeEntries(workOrderId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workOrders.timeEntries(workOrderId ?? ''),
    queryFn: () => api.get<WorkOrderTimeEntry[]>(`/work-orders/${workOrderId}/time-entries`),
    enabled: !!workOrderId,
    staleTime: 15 * 1000,
  });
}

export function useCreateWorkOrderTimeEntry(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkOrderTimeEntryData) =>
      api.post<WorkOrderTimeEntry>(`/work-orders/${workOrderId}/time-entries`, data),
    onSuccess: () => invalidateTimeEntryViews(queryClient, workOrderId),
  });
}

export function useUpdateWorkOrderTimeEntry(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, data }: { entryId: string; data: UpdateWorkOrderTimeEntryData }) =>
      api.patch<WorkOrderTimeEntry>(`/work-orders/${workOrderId}/time-entries/${entryId}`, data),
    onSuccess: () => invalidateTimeEntryViews(queryClient, workOrderId),
  });
}

export function useDeleteWorkOrderTimeEntry(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) =>
      api.delete(`/work-orders/${workOrderId}/time-entries/${entryId}`),
    onSuccess: () => invalidateTimeEntryViews(queryClient, workOrderId),
  });
}
